/**
 * Pure helpers for the CV score page ("Score & improve").
 *
 * The score, the checks and the gaps are counted by the server; these group
 * and rank them for display. Nothing here changes a number.
 */

/** The page's tabs, in order. `?tab=` holds the key. */
export const SCORE_TABS = ['overview', 'improvements', 'checklist', 'gaps', 'match'];

/** A tab key from the URL, or the first tab when it is missing or unknown. */
export function tabFrom(value) {
  return SCORE_TABS.includes(value) ? value : SCORE_TABS[0];
}

/**
 * The checklist in five groups a reader recognises. A check the server adds
 * later lands in "Other" rather than disappearing.
 */
export const CHECK_CATEGORIES = [
  { key: 'contact', label: 'Contact & links', icon: 'user', checks: ['contact', 'links'] },
  {
    key: 'structure',
    label: 'Structure & sections',
    icon: 'fileText',
    checks: ['education', 'experience'],
  },
  {
    key: 'content',
    label: 'Content & impact',
    icon: 'trendingUp',
    checks: ['quantified', 'action_verbs'],
  },
  { key: 'skills', label: 'Skills', icon: 'zap', checks: ['skills'] },
  { key: 'tone', label: 'Length & tone', icon: 'pencil', checks: ['length', 'first_person'] },
];

/** Checks grouped by category, each with points earned out of possible. */
export function groupChecks(checks) {
  const known = new Set(CHECK_CATEGORIES.flatMap((category) => category.checks));
  const groups = [
    ...CHECK_CATEGORIES.map((category) => ({
      ...category,
      items: (checks ?? []).filter((check) => category.checks.includes(check.key)),
    })),
    {
      key: 'other',
      label: 'Other',
      icon: 'check',
      items: (checks ?? []).filter((check) => !known.has(check.key)),
    },
  ];
  return groups
    .filter((group) => group.items.length > 0)
    .map(({ checks: _keys, ...group }) => ({
      ...group,
      earned: group.items.reduce((sum, check) => sum + (check.passed ? check.weight : 0), 0),
      possible: group.items.reduce((sum, check) => sum + check.weight, 0),
      passed: group.items.filter((check) => check.passed).length,
    }));
}

/** The score's band: label, tones and one plain sentence. Server grade wins. */
export const BANDS = {
  Strong: {
    tone: 'success',
    ring: 'text-success-500',
    bar: 'bg-success-500',
    verdict: 'Strong — a few improvements will make it stand out.',
  },
  Good: {
    tone: 'brand',
    ring: 'text-brand-600',
    bar: 'bg-brand-500',
    verdict: 'Good — fix the items below to reach the top band.',
  },
  'Needs work': {
    tone: 'warning',
    ring: 'text-warning-500',
    bar: 'bg-warning-500',
    verdict: 'Needs work — a handful of changes will lift it quickly.',
  },
  Weak: {
    tone: 'danger',
    ring: 'text-danger-500',
    bar: 'bg-danger-500',
    verdict: 'Weak — start with the top fixes below; each one adds points.',
  },
};

/** The grade for a score, by the server's thresholds (85 / 65 / 45). */
export function gradeFor(score) {
  if (score >= 85) return 'Strong';
  if (score >= 65) return 'Good';
  if (score >= 45) return 'Needs work';
  return 'Weak';
}

export function bandOf(score, grade) {
  const key = BANDS[grade] ? grade : gradeFor(score ?? 0);
  return { key, ...BANDS[key] };
}

/**
 * Up to `limit` highest-impact fixes across the failed checks, the important
 * gaps and the written suggestions. Each says where its detail lives
 * (`tab`, `anchor`) so a click can take the reader there.
 *
 * Impact: a failed check counts its points; an important gap counts as much
 * as the check it repeats (else 12); an important missing skill 11; the
 * first two written suggestions 9 and 8. Ties keep that order. A gap that
 * repeats a failed check is shown once, with the gap's fix.
 */
export function topFixes({ checks, fixes, gaps, suggestions }, limit = 3) {
  const candidates = [];
  const weightOf = Object.fromEntries((checks ?? []).map((check) => [check.key, check.weight]));
  const fixFor = Object.fromEntries((fixes ?? []).map((item) => [item.label, item.fix]));
  const covered = new Set();

  for (const item of gaps?.sections?.items ?? []) {
    if (item.severity !== 'important') continue;
    covered.add(item.key);
    candidates.push({
      id: `gap-${item.key}`,
      title: item.title,
      text: item.fix,
      severity: 'important',
      impact: weightOf[item.key] ?? 12,
      tab: 'gaps',
      anchor: 'gap-analysis',
    });
  }

  for (const check of checks ?? []) {
    if (check.passed || covered.has(check.key)) continue;
    candidates.push({
      id: `check-${check.key}`,
      title: check.label,
      text: fixFor[check.label] ?? check.detail,
      severity: check.weight >= 15 ? 'important' : 'suggested',
      impact: check.weight,
      tab: 'checklist',
      anchor: `check-${check.key}`,
    });
  }

  const skill = (gaps?.skills?.missing ?? []).find((item) => item.severity === 'important');
  if (skill) {
    candidates.push({
      id: `skill-${skill.skill.id}`,
      title: `Add ${skill.skill.name}`,
      text: `Asked for in ${skill.listings} of ${gaps.skills.fit?.open_listings ?? skill.listings} open listings in your field. Learn it, then show it on your CV.`,
      severity: 'important',
      impact: 11,
      tab: 'gaps',
      anchor: 'gap-analysis',
    });
  }

  (suggestions ?? []).slice(0, 2).forEach((item, index) => {
    candidates.push({
      id: `suggestion-${index}`,
      title: 'Suggested change',
      text: item.suggestion,
      severity: 'suggested',
      impact: 9 - index,
      tab: 'improvements',
      anchor: `improvement-${index}`,
    });
  });

  return candidates
    .map((candidate, order) => ({ candidate, order }))
    .sort((a, b) => b.candidate.impact - a.candidate.impact || a.order - b.order)
    .slice(0, limit)
    .map(({ candidate }) => candidate);
}
